import React, { useState, useContext, useEffect } from 'react'
import { useIonModal } from '@ionic/react'
import Context from '../context'
import { useUnauthAPI } from '../hooks/api.hooks'
import LocationErrorModal from './LocationErrorModal'
import SVG from '../components/SVG'
import { useRouter } from 'next/router'
import StoreListItem from './StoreListItem'

const StoreSearch = ({ cartAddData = {}, selectedStoreId = '', onChange = (data) => { }, handleDismiss = () => null, sku = null, isModal = true, findLocation = false, searchText = '' }) => {
  const { dispatch, state } = useContext(Context)
  const [stores, setStores] = useState([])
  const [searchTerm, setSearchTerm] = useState(searchText || '')
  const [loading, setLoading] = useState(false)
  const [fetchStatus, setFetchStatus] = useState('')
  const [disableButton, setDisableButton] = useState(false)
  const makeAPICall = useUnauthAPI()
  const router = useRouter()

  useEffect(() => {
    if (state.googleLoaded) {
      if (findLocation) {
        onUseMyLocationClick()
      } else if (searchTerm) {
        onGoClicked()
      } else if (!isModal) {
        fetchStores()
      }
    }
  }, [state.googleLoaded])

  const handleLocationErrorDismiss = () => {
    dismiss()
  }

  const [present, dismiss] = useIonModal(LocationErrorModal, {
    handleDismiss: handleLocationErrorDismiss
  })

  const fetchStores = (origin?) => {
    makeAPICall('stores', 'store', 'GET', {
      ...((origin?.lat && origin?.lng && !origin?.state) && { lat: origin.lat, lng: origin.lng }),
      ...(origin?.state && { state: origin.state }),
      ...(sku && { sku })
    }, {}).then(async (stores) => {
      if (stores.length) {
        setStores(stores)
        setFetchStatus('data')
      } else {
        setStores([])
        setFetchStatus('empty')
      }
      onChange({ origin, stores })
    })
  }

  useEffect(() => {
    if (findLocation) {
      onUseMyLocationClick()
    } else if (searchTerm) {
      onGoClicked()
    } else if (!isModal) {
      fetchStores()
    }
  }, [])

  const onGoClicked = (location?: any) => {
    setDisableButton(true)
    setTimeout(() => setDisableButton(false), 1000)
    if (location) {
      fetchStores(location)
    } else if (searchTerm) {
      !isModal && router.push({ query: `s=${searchTerm}` }, undefined, { shallow: true })
      if (state.googleLoaded) {
        const geocoder = new window.google.maps.Geocoder()
        geocoder.geocode({ address: searchTerm, componentRestrictions: { country: 'US' } }, (results, status) => {
          if (status === 'OK' && results.length) {
            if (results[0].types.includes('administrative_area_level_1')) {
              const state = results[0].address_components[0].short_name
              const lat = results[0].geometry.location.lat()
              const lng = results[0].geometry.location.lng()
              fetchStores({ state, lat, lng })
            } else {
              const lat = results[0].geometry.location.lat()
              const lng = results[0].geometry.location.lng()
              fetchStores({ lat, lng })
            }
          } else {
            setFetchStatus('empty')
            setStores([])
          }
        })
      }
    } else {
      if (!isModal && searchTerm === '') {
        router.push({ query: '' }, undefined, { shallow: true })
      }
      fetchStores()
    }
  }

  const error = () => {
    setLoading(false)
    setFetchStatus('location-error')
    present()
  }

  const onUseMyLocationClick = () => {
    setLoading(true)
    const options = {
      enableHighAccuracy: true,
      timeout: 5000,
      maximumAge: 0
    }

    const success = (pos) => {
      if (state.googleLoaded) {
        const crd = pos.coords
        const geocoder = new window.google.maps.Geocoder()
        const latlng = {
          lat: crd.latitude,
          lng: crd.longitude
        }

        geocoder.geocode({ location: latlng }, (results, status) => {
          if (status === 'OK') {
            if (results[0]) {
              const locations = results[0].address_components
              const zip = locations.find((x) => x.types.includes('postal_code'))
              !isModal && router.push({ query: `s=${zip.long_name}` }, undefined, { shallow: true })
              dispatch({
                type: 'ADD_LOCATION',
                payload: {
                  lat: crd.latitude,
                  lng: crd.longitude,
                  zip: zip.long_name
                }
              })
              setSearchTerm(zip.long_name)
              onGoClicked({ lat: crd.latitude, lng: crd.longitude })
              setLoading(false)
              setFetchStatus('empty')
            } else {
              dispatch({
                type: 'ADD_LOCATION',
                payload: { lat: crd.latitude, lng: crd.longitude }
              })
              setLoading(false)
              setFetchStatus('data')
            }
          } else {
            dispatch({
              type: 'ADD_LOCATION',
              payload: { lat: crd.latitude, lng: crd.longitude }
            })
            setLoading(false)
            setFetchStatus('empty')
          }
        })
      } else {
        error()
      }
    }
    navigator.geolocation.getCurrentPosition(success, error, options)
  }

  const getLoaderText = () => {
    if (fetchStatus === '') {
      return (
        <p>
          Please enter your<br />zipcode or city and state<br />
          to select a store
        </p>
      )
    } else if (fetchStatus === 'location-error') {
      return (
        <p>This browser does not have location permissions turned on. Please update and try again.</p>
      )
    } else if (fetchStatus === 'empty') {
      return (
        <p>No stores found within 50 miles of your location</p>
      )
    }
  }

  const renderContent = () => {
    return stores.map((store) => {
      return (
        <StoreListItem
          key={store.storenumber}
          store={store}
          selectedStoreId={selectedStoreId}
          showActions={true}
          sku={sku}
          cartAddData={cartAddData}
          handleDismiss={handleDismiss}
        />
      )
    })
  }

  const loader = () => {
    return (
      <div className="store-search__loader">
        <SVG name="alert-full" label="alert-full" size="xl" />
        {getLoaderText()}
      </div>
    )
  }

  const renderUseMyLocation = () => {
    return (
      <button className="store-search__use-my-location" onClick={onUseMyLocationClick}>
        <SVG name="icon-location" label="icon-location" size="base" />
        <div className={`store-search__location__text ${loading && 'store-search__location__text-animated'}`}>Use my location</div>
      </button>
    )
  }

  return (
    <div id="main-content" className="store-search">
      {isModal && <button onClick={() => handleDismiss()} className="store-search__btn-close" ><SVG name="close" label="Close" size="md" /></button>}
      <div className="store-search__container">
        {!isModal && <h3 className="store-search__title">find a store</h3>}
        {isModal && <div className="store-search__main-header">
          <h2>stores</h2>
        </div>}
        <form className="store-search__header" onSubmit={(e) => {
          e.preventDefault()
          onGoClicked()
        }}>
          <div className="store-search__header__text">
            <div className="input-wrapper">
              <label htmlFor="enterZipcode">Enter zipcode or city and state</label>
              <input
                id="enterZipcode"
                className="focus"
                type="search"
                aria-label="Enter zipcode"
                placeholder="Enter zipcode or city and state"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              ></input>
            </div>
          </div>
          <div className="store-search__header__button">
            <button
              type="submit"
              disabled={disableButton}
            >
              GO
            </button>
          </div>
        </form>
        {renderUseMyLocation()}
        {stores.length && fetchStatus !== 'empty' ? renderContent() : loader()}
      </div>
    </div>
  )
}

export default StoreSearch

export const getServerSideProps = async () => {
  return {
    props: {}
  }
}
